<script>
  import { onMount } from 'svelte';
  import CryptoList from './components/CryptoList/CryptoList.svelte';
  import Portfolio from './components/Portfolio/Portfolio.svelte';
  import TradingPanel from './components/TradingPanel/TradingPanel.svelte';
  import CandlestickChart from './components/CandlestickChart/CandlestickChart.svelte';
  import HeaderQuote from './components/HeaderQuote/HeaderQuote.svelte';
  import VaultManagement from './components/VaultManagement/VaultManagement.svelte';
  import UserManagement from './components/UserManagement/UserManagement.svelte';
  import Backtesting from './components/Backtesting/Backtesting.svelte';
  import TradingDashboard from './components/AutoTrading/TradingDashboard.svelte';
  import FakeHundredBill from './components/AutoTrading/FakeHundredBill.svelte';
  import RealHundredBill from './components/AutoTrading/RealHundredBill.svelte';
  import api from './lib/api.js';
  import { symbols, positions, selectedSymbol, currentQuote, ohlcvData, orders, loading, error } from './lib/store.js';
  import Orders from './components/Orders/Orders.svelte';
  import { isAuthenticated, user, login, logout, teardownAuth } from './lib/auth/auth.js';
  import { selectedOrgId, userRole, userRoleName, availableOrgs, isAdmin, canTrade, initializeOrgContext, resetOrgContext } from './lib/orgContext.js';
  import { ensureSse, stopSse, addSseSubscriber, sseConnectionState } from './lib/sseStore.js';
  import {
    isSandboxContext, activeExchangeId, sandboxCredsPresent, showBacktest, showPaperTrading,
    syncVenueContext, setTradingContext,
  } from './lib/venueContext.js';
  import { isCoinbaseSandbox } from './lib/venues.js';
  import { createScopedLoader } from './lib/scopedLoader.js';
  import { contextTarget, targetKey } from './lib/runContext.js';
  import K2Logo from './lib/assets/k2-logo-48.png';

  let isLoadingQuote = false;
  let symbolUnsubscribe;
  let refreshInterval;
  let lastQuoteUpdate = {};
  let sseUnsubscribe = null;

  let authSubscription;
  let loggingIn = false;
  let dataInitialized = false;
  let currentView = 'trading'; // 'trading', 'vault', 'backtest', 'autotrade', 'papertrade', or 'user-mgmt'
  let showProfileDropdown = false;
  let showOrgDropdown = false;
  let profileImageError = false;

  // Reset image error state when user changes
  $: if ($user) {
    profileImageError = false;
  }

  onMount(() => {
    authSubscription = isAuthenticated.subscribe(async (loggedIn) =>
    {
      if (loggedIn) {
        try {
          await initializeData();
        } catch (err) {
          console.error('Failed to initialize market data', err);
          error.set('Unable to load market data. Please try again.');
        }
      } else {
        teardownData();
        // Automatically trigger Clerk login when not authenticated
        handleLogin();
      }
    });

    return () => {
      if (authSubscription) authSubscription();
      teardownData(true);
      teardownAuth();
    };
  });

  // Reload positions and orders when selected org changes, and re-scope the SSE stream to the new org
  // (ADR-0007 U7 — the stream is org-scoped via a query param, never the JWT). ensureSse is idempotent:
  // same org → no-op; org changed → one reconnect scoped to the new org. The app's order/position refetch
  // is REGISTERED (addSseSubscriber in initializeData), never replaced by a dashboard mounting — the old
  // last-caller-wins startSse meant orders/positions silently stopped SSE-refreshing whenever a trading
  // dashboard was open.
  $: if ($selectedOrgId && dataInitialized) {
    loadPositions();
    loadOrders();
    // The context (Paper/Live) is a per-user+org preference gated on the org's sandbox credential —
    // re-resolve it on every org switch (ADR-0011 AC 8). This may flip $activeExchangeId, which the
    // venue-change block below reacts to (venue-scoped reloads + SSE re-scope).
    refreshVenueContext($selectedOrgId);
    ensureSse();
  }

  // ---- Venue-context reactions (ADR-0011 AC 8/9) ----
  // Switching Paper<->Live re-points EVERY venue-scoped read (symbols/quotes/positions/orders all default
  // to the context venue in api.js) and re-scopes the SSE stream (ensureSse keys on org+venue). The symbol
  // list is venue-specific (the sandbox trades ~13 products), so it reloads and re-picks a default symbol.
  let lastVenue = null;
  $: if (dataInitialized && $activeExchangeId !== lastVenue) {
    lastVenue = $activeExchangeId;
    loadSymbols(); // venue-scoped but NOT tenant-scoped — safe without an org.
    // Tenant-scoped reads require a resolved org (ADR-0023: omitting it is a 400, not "my own org").
    // The venue can resolve before the org context does, which is exactly how prod saw
    // `GET /api/holdings/positions?exchange=6` with no orgId. The org-change block below runs these
    // as soon as the org lands, so skipping here loses nothing.
    if ($selectedOrgId) {
      loadPositions();
      loadOrders();
      ensureSse();
    }
  }

  // Unmistakable mode salience (AC 9): the [SANDBOX] title prefix tracks the context store — the same
  // store every API call derives its venue from, so the title can never disagree with the venue traded.
  const BASE_TITLE = 'K2 - Professional Trading Dashboard';
  $: document.title = $isSandboxContext ? `[SANDBOX] ${BASE_TITLE}` : BASE_TITLE;

  // Live-context-only views (ADR-0012 AC 2/3): if the context flips to Sandbox while Backtest or
  // Paper Trading is open, leave the now-hidden view rather than rendering an orphaned surface.
  $: if ($isSandboxContext && (currentView === 'backtest' || currentView === 'papertrade')) {
    currentView = 'trading';
  }

  /** Resolve the org's sandbox-credential presence and (re)sync the persisted context for user+org. */
  async function refreshVenueContext(orgId) {
    // Same identity rule as the org context: the Clerk user id, never the email.
    const userId = $user?.id;
    if (!userId || !orgId) return;
    let hasSandboxCreds = false;
    try {
      const accounts = await api.getVaultAccounts(orgId);
      hasSandboxCreds = Array.isArray(accounts) && accounts.some((a) => isCoinbaseSandbox(a.exchgId));
    } catch (err) {
      console.error('Failed to check sandbox credentials; Sandbox context disabled for safety:', err);
    }
    // Stale-response guard (same class as the scopedLoader's): this runs unawaited from the
    // org-change block, so a slow vault read for a superseded org must not flip the venue — and,
    // worse, the persistence key — to that org's context after the user has moved on.
    if (orgId !== $selectedOrgId) return;
    // The email is the LEGACY storage-key identity (pre-PR-#61): passing it lets syncVenueContext
    // migrate a preference persisted under the old key instead of silently resetting the user to
    // the sandbox default — the TASK-20260818 F7 regression.
    syncVenueContext(userId, orgId, hasSandboxCreds, undefined, $user?.email ?? null);
  }

  // Human label for the live-connection indicator (KTD12).
  function sseLabel(state) {
    switch (state) {
      case 'connected': return 'Live';
      case 'connecting': return 'Connecting…';
      case 'reconnecting': return 'Reconnecting…';
      case 'stale': return 'Offline';
      default: return 'Live';
    }
  }

  // KTD7: SSE events are hints; the authoritative order/position state comes from a REST refetch (on every
  // (re)connect and debounced on push). This keeps the stores the single source of truth.
  async function refetchAuthoritative() {
    if (!dataInitialized) return;
    try {
      await Promise.all([loadOrders(), loadPositions()]);
    } catch (err) {
      console.error('SSE refetch failed', err);
    }
  }

  async function initializeData() {
    if (dataInitialized) {
      return;
    }

    dataInitialized = true;

    // Initialize org context (load user's role and orgs).
    // MUST be the Clerk user id, never the email: a user's personal org is the row where
    // OrgId == userId (UserOrgs/UserRoles are FKs to Orgs.OrgId), so matching on email never
    // matched and selectedOrgId fell through to orgs[0] or stayed null. Since the org scope became
    // mandatory (ADR-0023) that null surfaces as a 400 instead of silently reading the wrong org.
    const userId = $user?.id;
    if (userId) {
      await initializeOrgContext(userId, api.getMyOrgs.bind(api), api.getMyRole.bind(api));

      // Check if user has API credentials, if not navigate to Vault
      // Use selectedOrgId after org context is initialized
      if ($selectedOrgId) {
        await checkAndNavigateToVault($selectedOrgId);
        // Resolve the Paper/Live context BEFORE the first venue-scoped loads so they hit the right venue.
        await refreshVenueContext($selectedOrgId);
      }
    }

    lastVenue = $activeExchangeId; // the loads below already use the resolved venue — don't double-load
    await loadSymbols();
    await loadPositions();
    await loadOrders();

    // Register the app-shell's authoritative refetch with the shared SSE registry (once per init). The
    // connection itself is opened by the reactive ensureSse() block once the org context resolves.
    if (!sseUnsubscribe) {
      sseUnsubscribe = addSseSubscriber({ refetchAuthoritative });
    }

    if (!symbolUnsubscribe) {
      symbolUnsubscribe = selectedSymbol.subscribe(symbol => {
        if (symbol && !isLoadingQuote) {
          loadQuoteAndChart(symbol);
        }
      });
    }

    startPeriodicRefresh();
  }

  async function checkAndNavigateToVault(orgId) {
    try {
      // Check if user has any vault accounts configured
      const accounts = await api.getVaultAccounts(orgId);

      // If no accounts exist, navigate to Vault
      if (!accounts || accounts.length === 0) {
        console.log('No API credentials found, navigating to Vault');
        currentView = 'vault';
      }
    } catch (err) {
      console.error('Failed to check vault accounts:', err);
      // Don't navigate to vault on error - could be permission issue or network error
      // Let user access the app normally
    }
  }

  function teardownData(resetOnly = false) {
    dataInitialized = false;

    if (symbolUnsubscribe) {
      symbolUnsubscribe();
      symbolUnsubscribe = null;
    }

    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }

    // ADR-0007 U7: tear down the SSE stream on logout/unmount and drop the app-shell subscriber.
    stopSse();
    if (sseUnsubscribe) {
      sseUnsubscribe();
      sseUnsubscribe = null;
    }

    lastQuoteUpdate = {};
    isLoadingQuote = false;

    symbols.set([]);
    positions.set([]);
    orders.set([]);
    currentQuote.set(null);
    ohlcvData.set([]);
    selectedSymbol.set(null);
    loading.set(false);

    if (!resetOnly) {
      error.set(null);
    }
  }

  async function handleLogin() {
    if (loggingIn) {
      return;
    }

    loggingIn = true;

    try {
      await login();
    } catch (err) {
      console.error('Login failed', err);
    } finally {
      loggingIn = false;
    }
  }

  async function handleLogout() {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed', err);
    } finally {
      teardownData();
      resetOrgContext(); // Clear org context to prevent using previous user's data
      currentView = 'trading'; // Reset view to default
    }
  }

  async function loadSymbols() {
    // Venue-scoped (not tenant-scoped) — but the same stale-response rule applies (F8): the
    // startup venue flip fires this twice, and the slower default-venue response landing last
    // would render the sandbox catalog (and re-pick a sandbox default symbol) in the Live context.
    const requestedVenue = $activeExchangeId;
    try {
      loading.set(true);
      const data = await api.getSymbols();
      if (requestedVenue !== $activeExchangeId) return; // superseded — the venue block refires
      symbols.set(data);

      // Set default selected symbol if none is selected
      if (data && data.length > 0) {
        // Try to find BTC-USD or BTC/USD as default, otherwise use first symbol
        const defaultSymbol = data.find(s =>
          s.symbol === 'BTC-USD' || s.symbol === 'BTC/USD' || s.symbol === 'BTC-USDT'
        ) || data[0];

        if (defaultSymbol) {
          selectedSymbol.set(defaultSymbol.symbol);
        }
      }
    } catch (err) {
      error.set('Failed to load symbols: ' + err.message);
    } finally {
      loading.set(false);
    }
  }

  // Both tenant-scoped loaders share the ONE scoped-load predicate — see createScopedLoader
  // (TASK-20260818 F1/F8): skip without an org; drop any response from a superseded org or venue.
  const loadPositions = createScopedLoader({
    getOrgId: () => $selectedOrgId,
    getScope: () => [targetKey($contextTarget)], // the reads default to the context target (venue + mode)
    load: (orgId) => api.getPositions(undefined, orgId), // mode=Live: the manual surfaces are the venue account
    // Ensure data is always an array, never null
    apply: (data) => positions.set(data || []),
    onError: (err) => {
      console.error('Failed to load positions:', err);
      // Set to empty array on error to prevent null reference errors
      positions.set([]);
    },
  });

  async function updateSymbolPrice(symbol, quote) {
    if (!quote) return;
    
    // Update the symbols store with the latest price data
    symbols.update(syms => {
      return syms.map(s => {
        if (s.symbol === symbol) {
          return {
            ...s,
            lastPrice: quote.lastTradePrice || s.lastPrice,
            bid: quote.bid || s.bid,
            ask: quote.ask || s.ask,
            baseVol: quote.baseVol24Hrs || s.baseVol,
            priceChange: quote.priceChangePercentage || s.priceChange,
            highPrice: quote.highPrice24Hrs || s.highPrice,
            lowPrice: quote.lowPrice24Hrs || s.lowPrice
          };
        }
        return s;
      });
    });
  }

  async function loadQuoteAndChart(symbol) {
    if (isLoadingQuote) return;
    
    try {
      isLoadingQuote = true;
      loading.set(true);
      
      // Load quote
      try {
        const quote = await api.getQuote(symbol);
        if (quote) {
          currentQuote.set(quote);
          updateSymbolPrice(symbol, quote);
          lastQuoteUpdate[symbol] = Date.now();
        } else {
          console.warn('No quote data received for:', symbol);
          currentQuote.set(null);
        }
      } catch (quoteError) {
        console.error('Failed to load quote for', symbol, ':', quoteError);
        currentQuote.set(null);
      }
      
      // Load OHLCV data for chart
      try {
        const ohlcv = await api.getOHLCV(symbol);
        if (ohlcv) {
          ohlcvData.set(ohlcv);
        } else {
          console.warn('No OHLCV data received for:', symbol);
          ohlcvData.set([]);
        }
      } catch (ohlcvError) {
        console.error('Failed to load OHLCV for', symbol, ':', ohlcvError);
        ohlcvData.set([]);
      }
    } catch (err) {
      error.set('Failed to load market data: ' + err.message);
    } finally {
      loading.set(false);
      isLoadingQuote = false;
    }
  }

  // THE single owner of the `orders` store. Orders.svelte used to define a second, UNSCOPED
  // loadOrders() that wrote this same store on mount and on every Refresh click, clobbering the
  // correctly-scoped data with the caller's own org's — the reported bug. One store, one writer.
  const loadOrders = createScopedLoader({
    getOrgId: () => $selectedOrgId,
    getScope: () => [targetKey($contextTarget)], // the reads default to the context target (venue + mode)
    load: (orgId) => api.getOrders('', '', undefined, orgId),
    apply: (data) => {
      const list = Array.isArray(data) ? data : data ? [data] : [];
      // Newest first (this sort came from the deleted duplicate loader — preserved deliberately).
      list.sort((a, b) => new Date(b.filledOrCancelledTime || b.createdAt)
        - new Date(a.filledOrCancelledTime || a.createdAt));
      orders.set(list);
    },
    onError: (error) => {
      console.error('Failed to load orders:', error);
      orders.set([]);
    },
  });

  async function refreshQuote(symbol) {
    // Prevent duplicate calls within 2 seconds
    if (lastQuoteUpdate[symbol] && Date.now() - lastQuoteUpdate[symbol] < 2000) {
      return;
    }
    
    try {
      const quote = await api.getQuote(symbol);
      if (quote) {
        // If this is the selected symbol, update currentQuote
        if (symbol === $selectedSymbol) {
          currentQuote.set(quote);
        }
        updateSymbolPrice(symbol, quote);
        lastQuoteUpdate[symbol] = Date.now();
      }
    } catch (err) {
      console.error(`Failed to refresh quote for ${symbol}:`, err);
    }
  }

  function startPeriodicRefresh() {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }

    refreshInterval = setInterval(async () => {
      if (!dataInitialized) {
        return;
      }

      // Refresh positions
      await loadPositions();
      
      // Refresh current selected symbol
      if ($selectedSymbol) {
        await refreshQuote($selectedSymbol);
      }
      
      // Get unique portfolio currencies that need price updates
      const portfolioSymbols = new Set();
      const currentPositions = $positions;
      
      currentPositions.forEach(position => {
        const base = position.baseCurrency?.toUpperCase();
        if (base && base !== 'USD' && base !== 'USDT' && base !== 'USDC') {
          // First, check if we have a direct USD pair
          const usdSymbol = `${base}/USD`;
          const hasUsdPair = $symbols.some(s => s.symbol === usdSymbol);
          
          if (hasUsdPair) {
            portfolioSymbols.add(usdSymbol);
          } else {
            // Only check for USDT as fallback if USD doesn't exist
            const usdtSymbol = `${base}/USDT`;
            const hasUsdtPair = $symbols.some(s => s.symbol === usdtSymbol);
            if (hasUsdtPair) {
              portfolioSymbols.add(usdtSymbol);
            } else {
              // Final fallback to USDC
              const usdcSymbol = `${base}/USDC`;
              const hasUsdcPair = $symbols.some(s => s.symbol === usdcSymbol);
              if (hasUsdcPair) {
                portfolioSymbols.add(usdcSymbol);
              }
            }
          }
        }
      });
      
      // Remove the currently selected symbol to avoid duplicate calls
      portfolioSymbols.delete($selectedSymbol);
      
      // Update prices for portfolio symbols (limit to 3 to avoid too many API calls)
      const symbolsToUpdate = Array.from(portfolioSymbols).slice(0, 3);
      for (const symbol of symbolsToUpdate) {
        await refreshQuote(symbol);
        // Small delay between calls
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }, 60000); // ADR-0007 KTD7: slow safety backstop (60s). SSE pushes drive live freshness; this poll
               // only covers a missed event between reconnects. (Was 10s before SSE.)
  }
</script>

{#if $isAuthenticated}
  <!-- The .theme-paper token set (cool-blue) is the SANDBOX context's salience treatment — the CSS
       class name predates the ADR-0012 rename and is kept (the visual treatment is unchanged). -->
  <main class="app-container" class:theme-paper={$isSandboxContext}>
    <header class="app-header">
      <div class="header-left">
        <div class="app-logo">
          <img src={K2Logo} alt="K2 Capital" width="48" height="48" />
        </div>

        <HeaderQuote />

        <!-- Org Selector - Always show (exclude in User Management view) -->
        {#if currentView !== 'user-mgmt' && $availableOrgs && $availableOrgs.length > 0}
          <div class="org-selector">
            <span class="org-label">Org:</span>
            <button
              class="org-button"
              on:click={() => showOrgDropdown = !showOrgDropdown}
            >
              <span class="org-name">
                {$availableOrgs.find(o => o.orgId === $selectedOrgId)?.orgName || 'Select Org'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" class="dropdown-arrow">
                <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>

            {#if showOrgDropdown}
              <div class="org-dropdown">
                {#each $availableOrgs as org}
                  <button
                    class="dropdown-item"
                    class:selected={org.orgId === $selectedOrgId}
                    on:click={() => {
                      selectedOrgId.set(org.orgId);
                      showOrgDropdown = false;
                      // Reload data for the new org
                      loadPositions();
                      loadOrders();
                    }}
                  >
                    <span>{org.orgName}</span>
                    {#if org.orgId === $selectedOrgId}
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                      </svg>
                    {/if}
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        {/if}

        <!-- ADR-0007 KTD12: live-connection indicator. The stale/degraded state is VISIBLE so a trader
             never reads hours-old data as live. A11y: role=status + a polite label. -->
        <div
          class="sse-indicator sse-{$sseConnectionState}"
          role="status"
          aria-live="polite"
          title={sseLabel($sseConnectionState)}
        >
          <span class="sse-dot" aria-hidden="true"></span>
          <span class="sse-text">{sseLabel($sseConnectionState)}</span>
        </div>
      </div>

      <div class="header-right">
        <nav class="header-nav">
          <button
            class="nav-button"
            class:active={currentView === 'trading'}
            on:click={() => currentView = 'trading'}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2 18l4-4 4 4 8-8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <circle cx="6" cy="14" r="1.5" fill="currentColor"/>
              <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
              <circle cx="18" cy="2" r="1.5" fill="currentColor"/>
            </svg>
            Markets
          </button>
          <!-- Vault + User Mgmt moved into the user-icon dropdown (TASK-20260721 items 1-2). -->
          <!-- Live-context only (ADR-0012 AC 2): the Sandbox context is for execution-mechanics testing,
               where a backtest is out of place. NOT gated on $canTrade — that store only switches layout
               within Trading, never nav. -->
          {#if $showBacktest}
            <button
              class="nav-button"
              class:active={currentView === 'backtest'}
              on:click={() => currentView = 'backtest'}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 3v14h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M6 12l3-4 3 2 4-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Backtest
            </button>
          {/if}
          <!-- One auto-trading surface (ADR-0011 AC 10): which venue it points at — Sandbox or Live —
               is the HEADER CONTEXT TOGGLE's job, not a second menu item. The label is CONTEXT-DERIVED
               (TASK-20260721 item 3, venue honesty): a static "Live Trading" would mislabel the
               Sandbox-context dashboard. -->
          <button
            class="nav-button"
            class:active={currentView === 'autotrade'}
            on:click={() => currentView = 'autotrade'}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 10h14M10 3v14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2"/>
            </svg>
            {$isSandboxContext ? 'Sandbox Trading' : 'Live Trading'}
          </button>
          <!-- The revived Paper Trading (ADR-0012 AC 3; ADR-0040): the PAPER RUN MODE on Coinbase — simulated
               fills priced by walking the LIVE order book, for strategy-performance measurement. A
               Live-context view (never visible in Sandbox context); same dashboard, scoped to the
               (Coinbase, Paper) target. -->
          {#if $showPaperTrading}
            <button
              class="nav-button"
              class:active={currentView === 'papertrade'}
              on:click={() => currentView = 'papertrade'}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" stroke-width="2" stroke-dasharray="3 2"/>
                <path d="M6 13l3-4 2 2 3-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Paper Trading
            </button>
          {/if}
        </nav>

        <!-- Sandbox/Live context toggle (ADR-0011 AC 8/9; renamed by ADR-0012 — venue 6 is honestly
             named Sandbox; the revived Paper Trading is a Live-context MENU ITEM, not a toggle state).
             ONE store drives the venue of every API call, the SSE scope, the app theme, the bill, and
             the title — they can never disagree. Sandbox is disabled (with a Vault hint) until the org
             has CBX credentials. The FAKE bill stays — fake funds either way. -->
        <div class="venue-toggle" role="group" aria-label="Trading context">
          <span class="venue-bill" aria-hidden="true">
            {#if $isSandboxContext}
              <FakeHundredBill height={26} />
            {:else}
              <RealHundredBill height={26} />
            {/if}
          </span>
          <button
            type="button"
            class="venue-btn venue-btn-paper"
            class:active={$isSandboxContext}
            disabled={!$sandboxCredsPresent}
            aria-pressed={$isSandboxContext}
            title={$sandboxCredsPresent
              ? 'Sandbox — the Coinbase Exchange sandbox venue (fake funds, real matching engine)'
              : 'Set up Coinbase Sandbox keys in the Vault to enable the Sandbox context'}
            on:click={() => setTradingContext('sandbox')}
          >
            Sandbox
          </button>
          <button
            type="button"
            class="venue-btn venue-btn-live"
            class:active={!$isSandboxContext}
            aria-pressed={!$isSandboxContext}
            title="Live trading — real money on Coinbase"
            on:click={() => setTradingContext('live')}
          >
            Live
            {#if !$isSandboxContext}<span class="nav-live-chip" aria-hidden="true">LIVE</span>{/if}
          </button>
        </div>

        {#if $user}
          <div class="user-profile">
            <button
              class="profile-button"
              on:click={() => showProfileDropdown = !showProfileDropdown}
            >
              {#if $user.imageUrl && !profileImageError}
                <img
                  src={$user.imageUrl}
                  alt={$user.name}
                  class="profile-image"
                  on:error={() => profileImageError = true}
                />
              {:else}
                <div class="profile-placeholder">
                  {$user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              {/if}
              <span class="profile-name">{$user.name}</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" class="dropdown-arrow">
                <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>

            {#if showProfileDropdown}
              <div class="profile-dropdown">
                <a
                  href="https://myaccount.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="dropdown-item"
                  on:click={() => showProfileDropdown = false}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="5" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M3 13c0-2.5 2-4 5-4s5 1.5 5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  Profile
                </a>
                <!-- Vault + User Mgmt live here now (TASK-20260721 items 1-2): Profile → Vault →
                     User Mgmt (admin-only) → Sign out. Each item routes AND closes the dropdown. -->
                <button
                  class="dropdown-item"
                  class:active={currentView === 'vault'}
                  on:click={() => { showProfileDropdown = false; currentView = 'vault'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <rect x="3" y="6" width="14" height="12" rx="1" stroke="currentColor" stroke-width="2"/>
                    <path d="M7 6V4a3 3 0 016 0v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <circle cx="10" cy="12" r="1.5" fill="currentColor"/>
                  </svg>
                  Vault
                </button>
                {#if $isAdmin}
                  <button
                    class="dropdown-item"
                    class:active={currentView === 'user-mgmt'}
                    on:click={() => { showProfileDropdown = false; currentView = 'user-mgmt'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="7" r="3" stroke="currentColor" stroke-width="1.5"/>
                      <path d="M4 18c0-3 2.5-5 6-5s6 2 6 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                    User Mgmt
                  </button>
                {/if}
                <button class="dropdown-item" on:click={() => { showProfileDropdown = false; handleLogout(); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  Sign out
                </button>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </header>

    {#if $error}
      <div class="error-banner">
        {$error}
        <button on:click={() => error.set(null)}>×</button>
      </div>
    {/if}

    <div class="dashboard">
      {#if currentView === 'trading'}
        <aside class="dashboard-sidebar">
          <CryptoList />
        </aside>

        <div class="dashboard-main" class:client-layout={!$canTrade}>
          {#if $canTrade}
            <div class="dashboard-panels">
              <div class="trading-section">
                <!-- The App-owned guarded loaders are the ONLY writers of the orders/positions
                     stores (one store, one writer — TASK-20260817/20260818): the panel asks the
                     owner to refresh instead of fetching-and-setting the stores itself. -->
                <TradingPanel refreshOrders={loadOrders} refreshPositions={loadPositions} />
              </div>
              <div class="portfolio-section">
                <Portfolio />
              </div>
            </div>

            <div class="dashboard-chart-section">
              <div class="orders-section">
                <Orders onRefresh={loadOrders} />
              </div>
              <div class="chart-wrapper">
                <CandlestickChart />
              </div>
            </div>
          {:else}
            <div class="orders-section-expanded">
              <Orders onRefresh={loadOrders} />
            </div>
            <div class="portfolio-section">
              <Portfolio />
            </div>
            <div class="chart-wrapper">
              <CandlestickChart />
            </div>
          {/if}
        </div>
      {:else if currentView === 'vault'}
        <div class="vault-view">
          <VaultManagement />
        </div>
      {:else if currentView === 'backtest'}
        <div class="backtest-view">
          <Backtesting />
        </div>
      {:else if currentView === 'autotrade'}
        <div class="autotrade-view">
          <TradingDashboard />
        </div>
      {:else if currentView === 'papertrade'}
        <div class="autotrade-view">
          <TradingDashboard view="paper" />
        </div>
      {:else if currentView === 'user-mgmt'}
        <div class="user-mgmt-view">
          <UserManagement />
        </div>
      {/if}
    </div>
  </main>
{:else}
  <!-- Clerk will automatically show its sign-in UI -->
  <section class="auth-loading">
    <div class="auth-loading-content">
      <p>Loading authentication...</p>
    </div>
  </section>
{/if}

<style>
  @import './App.css';
</style>
